import React, { useEffect, useRef } from "react";
import { SerializedError } from "@reduxjs/toolkit";

import { IAutosuggest, ISearchResult } from "types";
import { normalizeSearchResult } from "utils";
import { i18nService } from "services";
import { SearchFilterItem, Loader } from "components";

interface SearchFilterDropdownProps {
  results: IAutosuggest[] | ISearchResult[];
  isLoading?: boolean;
  error?: SerializedError | null;
}

export const SearchFilterDropdown: React.FC<SearchFilterDropdownProps> = ({
  results,
  isLoading,
  error,
}) => {
  const searchFilterDropdownRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const dropdownRef = searchFilterDropdownRef.current;

    const onKeyDown = (event: KeyboardEvent) => {
      const activeElement = document.activeElement;
      const firstListItem = dropdownRef?.querySelector(
        ".search-filter-dropdown__list li:first-child > .search-filter-item"
      ) as HTMLElement | null;
      const searchFilterItems = dropdownRef?.querySelectorAll(
        ".search-filter-item"
      ) as NodeListOf<HTMLElement>;

      if (!searchFilterItems?.length) return;

      let index = Array.from(searchFilterItems).indexOf(
        activeElement as HTMLElement
      );

      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          if (index > 0) {
            searchFilterItems[index - 1]?.focus();
          } else {
            searchFilterItems[searchFilterItems.length - 1]?.focus(); // Loop to the last item
          }
          break;
        case "ArrowDown":
          event.preventDefault();
          if (index === -1 && firstListItem) {
            firstListItem.focus();
          } else if (index < searchFilterItems.length - 1) {
            searchFilterItems[index + 1]?.focus();
          } else {
            searchFilterItems[0]?.focus(); // Loop to the first item
          }
          break;
        default:
        // do nothing
      }
    };

    if (dropdownRef) {
      window.addEventListener("keydown", onKeyDown, false);
    }

    return () => {
      if (dropdownRef) {
        window.removeEventListener("keydown", onKeyDown);
      }
    };
  }, [searchFilterDropdownRef]);

  const renderInner = () => {
    if (error) {
      return (
        <p
          role="alert"
          className="search-filter-dropdown__message form-message is-error"
        >
          {i18nService.copy["siteSearch.error"]}
        </p>
      );
    }
    if (isLoading) {
      return (
        <div role="alert" className="search-filter-dropdown__loader">
          <Loader size="s" />
        </div>
      );
    }

    if (results?.length === 0) {
      return (
        <p className="search-filter-dropdown__message form-message">
          {i18nService.copy["siteSearch.noResults"]}
        </p>
      );
    }

    return (
      <ul className="search-filter-dropdown__list">
        {results.map((result: IAutosuggest | ISearchResult, index: number) => {
          // ISearchResult
          if ("document" in result) {
            return (
              <li key={`search-filter-dropdown-item-${index}`}>
                <SearchFilterItem
                  key={index}
                  {...normalizeSearchResult(result)}
                />
              </li>
            );
          }

          // IAutosuggest
          return (
            <li key={`search-filter-dropdown-item-${index}`}>
              <SearchFilterItem key={index} {...result} />
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="search-filter-dropdown" ref={searchFilterDropdownRef}>
      <div className="search-filter-dropdown__inner">{renderInner()}</div>
    </div>
  );
};
